import { useState, useEffect } from "react";
import {
  PromoCode,
  promoCodeService,
  PromoCodeStat,
  UpdatePromoData,
} from "../../../services/administration/codepromoService";
import useToken from "../../auth/useToken";

interface UsePromoCodeProps {
  userId: string;
}

export default function usePromoCodes({ userId }: UsePromoCodeProps) {
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [promoCodeStat, setPromoCodeStat] = useState<PromoCodeStat | null>(null);
  const [referrals, setReferrals] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useToken();

  const fetchPromoCode = async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const data = await promoCodeService.getPromoCode(userId, token);
      setPromoCode(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoStats = async () => {
    if (!userId || !token) return;
    setLoading(true);
    try {
      const data = await promoCodeService.getUserPromoStats(userId, token);
      setPromoCodeStat(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePromoCode = async (newCode: string) => {
    setLoading(true);
    try {
      const updated = await promoCodeService.updatePromoCode(userId, newCode, token);
      setPromoCode(updated);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateRewardPerInvite = async (amount_received_parrain: number, amount_received_filleule: number) => {
    setLoading(true);
    try {
      const updated = await promoCodeService.updateUserRewards(
        userId,
        amount_received_parrain,
        amount_received_filleule,
        token
      );
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateShareMessage = async (shareMessage: string) => {
    setLoading(true);
    try {
      const updated = await promoCodeService.updateShareMessage(userId, shareMessage, token);
      setPromoCode(updated);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePromoStatus = async (status: "ACTIVE" | "INACTIVE") => {
    setLoading(true);
    try {
      const updated = await promoCodeService.updatePromoStatus(userId, status, token);
      setPromoCode(updated);
      return updated;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async () => {
    if (!userId || !token) return;
    try {
      const data = await promoCodeService.getUserReferrals(userId, token);
      setReferrals(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (userId && token) {
      fetchPromoCode();
      fetchPromoStats();
      fetchReferrals();
    }
  }, [userId, token]);

  const refresh = () => {
    if (userId && token) {
      fetchPromoCode();
      fetchPromoStats();
      fetchReferrals();
    }
  };

  return {
    promoCode,
    promoCodeStat,
    referrals,
    loading,
    error,
    fetchPromoCode,
    fetchPromoStats,
    updatePromoCode,
    updateRewardPerInvite,
    updateShareMessage,
    updatePromoStatus,
    refresh,
  };
}
